'use strict';

document.addEventListener('DOMContentLoaded', () => {
  if (typeof theme === 'undefined') {
    console.error('Theme object is not defined. Ensure theme.js is loaded before cart.js.');
    return;
  }

  // === Custom Elements ===

  // CartDrawer
  if (!customElements.get('cart-drawer')) {
    class CartDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();
        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener('is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        theme.a11y.removeTrapFocus();
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(theme.utils.sectionId(this));
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
          <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
            <div class="drawer__empty grid gap-5 md:gap-8">
              <h2 class="drawer__empty-text heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</h2>
            </div>
          </div>
        `;
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);
        if (this.tabList) {
          this.tabList.reset();
          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }

    customElements.define('cart-drawer', CartDrawer);
  }

  // CartQuantity
  if (!customElements.get('cart-quantity')) {
    class CartQuantity extends QuantitySelector {
      constructor() {
        super();
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        super.connectedCallback();
        this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber();
        }
      }

      validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
          const buttonMinus = this.querySelector('[name="minus"]');
          if (buttonMinus) {
            buttonMinus.disabled = parseInt(value) <= parseInt(this.input.min);
          }
        }
        if (this.input.max) {
          const buttonPlus = this.querySelector('[name="plus"]');
          if (buttonPlus) {
            buttonPlus.disabled = parseInt(value) >= parseInt(this.input.max);
          }
        }
      }
    }

    customElements.define('cart-quantity', CartQuantity);
  }

  // CartRemoveButton
  if (!customElements.get('cart-remove-button')) {
    class CartRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          event.preventDefault();
          const cartItems = this.closest('cart-items');
          if (cartItems) {
            cartItems.updateQuantity(this.dataset.index, 0);
          } else {
            console.error('CartItems element not found for CartRemoveButton.');
          }
        });
      }
    }

    customElements.define('cart-remove-button', CartRemoveButton, { extends: 'a' });
  }

  // CartItems
  if (!customElements.get('cart-items')) {
    class CartItems extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        theme.a11y.removeTrapFocus();
      }

      onChange(event) {
        this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        const sectionId = theme.utils.sectionId(this);
        const sectionToRender = new DOMParser().parseFromString(event.cart.sections?.[sectionId] || '', 'text/html');

        const miniCart = document.querySelector(`#MiniCart-${sectionId}`);
        if (miniCart) {
          const updatedElement = sectionToRender.querySelector(`#MiniCart-${sectionId}`);
          if (updatedElement) miniCart.innerHTML = updatedElement.innerHTML;
        }

        const mainCart = document.querySelector(`#MainCart-${sectionId}`);
        if (mainCart) {
          const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
          if (updatedElement) {
            mainCart.innerHTML = updatedElement.innerHTML;
          } else {
            mainCart.closest('.cart').classList.add('is-empty');
            mainCart.remove();
          }
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        const focusElement = lineItem?.querySelector(`[name="${event.name}"]`) ?? (event.cart.item_count === 0 ? document.querySelector('.empty-state__link') : (miniCart?.querySelector('.horizontal-product__title') || mainCart?.querySelector('.cart__item-title')));
        if (focusElement) theme.a11y.trapFocus(mainCart || miniCart, focusElement);

        if (typeof Shopify !== 'undefined' && Shopify.theme?.cartDrawer?.open) {
          Shopify.theme.cartDrawer.open();
        } else {
          document.dispatchEvent(new CustomEvent('cart:drawer-open'));
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: event.cart } }));
      }

      onCartError(errors, target) {
        console.error('Cart update error:', errors);
        if (target) {
          this.updateQuantity(target.dataset.index, target.defaultValue, document.activeElement.getAttribute('name'), target);
        } else {
          window.location.href = theme.routes.cart_url;
        }
        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
        fetch(`${theme.routes.cart_change_url}`, { ...theme.utils.fetchConfig(), headers: { ...theme.utils.fetchConfig().headers, 'X-Cart-Token': window.cartToken }, body: JSON.stringify({ line, quantity, sections: sectionsToBundle }) })
          .then(response => response.json())
          .then(parsedState => theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState, target, line, name }))
          .catch(error => console.error('Error updating cart quantity:', error));
      }

      enableLoading(line) {
        const loader = document.getElementById(`Loader-${theme.utils.sectionId(this)}-${line}`);
        if (loader) loader.hidden = false;
      }
    }

    customElements.define('cart-items', CartItems);
  }

  // CartNote
  if (!customElements.get('cart-note')) {
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), body }).catch(error => console.error('Error updating cart note:', error));
      }
    }

    customElements.define('cart-note', CartNote);
  }

  // MainCart
  if (!customElements.get('main-cart')) {
    class MainCart extends HTMLElement {
      constructor() {
        super();
        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(theme.utils.sectionId(this));
      }
    }

    customElements.define('main-cart', MainCart);
  }
});
